import { supabase } from '../lib/supabase';
import type {
    CobrancaAcaoManual, CobrancaAcaoStatus, CobrancaCliente, CobrancaStatusNegociacao,
    DemandaEsteira, EsteiraAtribuicao, EsteiraFila, EsteiraGrupo, EsteiraKPIs,
} from '../../types';
import {
    normalizarAcaoManual, normalizarCobrancaCliente, normalizarDistrato,
    normalizarDocumento, ordenarDemandas, calcularKPIs, donoEfetivo,
    filtrarReguaComDebito, filtrarMoraSemDebito, dividaPorNome, marcarDividaEmOutroCpf,
    agruparPorCliente, marcarAgendados, filasDa,
    compartilhadaCom, linhaDeQuemSoDeve, marcarDistrato, escolherUcsDoCliente, comUcs, ehDaPessoa,
    foraDaReguaPorTratamento, aplicarUltimaAcao, maisRecente, trabalhadaHoje, diaLocal, estaParado,
    type LogDistrato, type LogDocumento, type LinhaCobrancaCliente, type DebitoCliente,
    type UltimoContato, type OrdemPainel,
} from '../../views/Financeiro/EsteiraCobranca/normalizar';
import { ensureCobrancaCliente } from '../lib/cobrancaCliente';
import {
    camposDoDesfecho, retornoDoDesfecho, statusDoDesfecho, type CamposDesfecho,
} from '../lib/desfechoContato';
import { atualizarStatusCobrancaCliente } from './api';

/**
 * Camada de dados da esteira.
 *
 * Hoje ela lê as cinco tabelas de origem e unifica no client — de propósito: a
 * tela foi construída antes de qualquer mudança de schema, para não travar a
 * entrega numa migration. Quando a view `vw_esteira_demandas` existir, só a
 * implementação de `fetchEsteira` muda; o contrato (`DemandaEsteira`) e a tela
 * ficam iguais.
 *
 * Os volumes envolvidos são pequenos (centenas de linhas por tabela) e cada
 * origem já vem pré-filtrada pelo que é demanda de verdade — cancelado e
 * encerrado não saem do banco.
 */

const PAGINA = 1000;

/** Pagina até o fim. O PostgREST corta em 1000 por resposta. */
async function todasAsLinhas<T>(
    montar: () => any,
): Promise<T[]> {
    const acumulado: T[] = [];
    for (let inicio = 0; ; inicio += PAGINA) {
        const { data, error } = await montar().range(inicio, inicio + PAGINA - 1);
        if (error) throw error;
        const lote = (data ?? []) as T[];
        acumulado.push(...lote);
        if (lote.length < PAGINA) return acumulado;
    }
}

export interface EsteiraFiltros {
    grupo?: EsteiraGrupo | '';
    fila?: EsteiraFila | '';
    status?: CobrancaAcaoStatus | '';
    statusNegociacao?: CobrancaStatusNegociacao | '';
    responsavelId?: string;
    search?: string;
    /** Só demandas paradas há pelo menos N dias. */
    minDiasParado?: number;
    /** Só o que ninguém assumiu — a fila de onde se puxa trabalho. */
    apenasSemDono?: boolean;
    /**
     * Só os clientes com retorno agendado para depois de hoje. Sem isto eles
     * ficam fora da lista: saíram do painel até a data (#6147).
     */
    apenasAgendados?: boolean;
    /**
     * Só o que é do distrato. Sem isto fica fora da lista: quem tem distrato
     * é tratado em Cancelamentos, não na rotina de cobrança (07/10/2026).
     */
    apenasDistrato?: boolean;
    /** Só o que foi trabalhado hoje — inclusive o que saiu da lista por ter retorno marcado (#6211). */
    apenasTrabalhadasHoje?: boolean;
    /** Atraso mais recente primeiro (padrão, reunião de 23/10/2026) ou o mais antigo. */
    ordem?: OrdemPainel;
}

/**
 * Débito vencido por cliente, do banco. Devolve `null` se a função não existir
 * ou falhar — a tela então mostra tudo como antes, sem esconder nada por um
 * palpite.
 */
async function carregarDebitos(): Promise<Map<string, DebitoCliente> | null> {
    try {
        const { data, error } = await supabase.rpc('cobranca_debito_por_cliente');
        if (error) throw error;
        // As três últimas colunas vieram em 07/10/2026; a versão anterior da
        // função não as tem e o painel segue funcionando sem elas.
        const linhas = (data ?? []) as Array<{
            cpf_cnpj: string; faturas: number[] | null; valor: number | string | null;
            vencimento_mais_antigo: string | null;
            vencimento_mais_recente?: string | null;
            faturas_distrato?: number[] | null;
            cliente_nome?: string | null;
        }>;
        return new Map(linhas.map(l => [l.cpf_cnpj, {
            faturas: (l.faturas ?? []).map(Number),
            valor: Number(l.valor ?? 0),
            vencimento_mais_antigo: l.vencimento_mais_antigo ?? null,
            vencimento_mais_recente: l.vencimento_mais_recente ?? null,
            faturas_distrato: (l.faturas_distrato ?? []).map(Number),
            cliente_nome: l.cliente_nome ?? null,
        }]));
    } catch (e) {
        console.error('Débito por cliente indisponível — painel sem o filtro de quitados:', e);
        return null;
    }
}

/**
 * UCs de cada cliente, pelo CPF/CNPJ só com dígitos (#6201): as das faturas
 * vencidas em aberto ou, se não deve nada, as do cadastro. Falhou: mapa vazio
 * e o card fica como antes, sem UC.
 */
async function carregarUcsPorCpf(
    debitos: Map<string, DebitoCliente> | null,
    cpfsDosCards: Array<string | null>,
): Promise<{
    ucs: Map<string, Array<{ numero: string; cancelada?: boolean }>>;
    faturas: Map<string, NonNullable<DemandaEsteira['faturas_abertas']>>;
    /** Nome do cliente pelo cadastro das UCs em que o CPF é o cliente. */
    nomes: Map<string, string>;
}> {
    const digitos = (v: unknown) => String(v ?? '').replace(/\D/g, '');
    try {
        const ids = debitos ? [...new Set([...debitos.values()].flatMap(d => d.faturas))] : [];
        const lotes: number[][] = [];
        for (let i = 0; i < ids.length; i += 300) lotes.push(ids.slice(i, i + 300));
        const [faturas, cadastro] = await Promise.all([
            Promise.all(lotes.map(async lote => {
                // Mês, valor e vencimento vão para a lista de débitos do card (#6211) —
                // mesma consulta, sem viagem extra.
                const { data, error } = await supabase.from('faturas')
                    .select('id, uc_codigo, mes_referencia, valor_boleto_vigor, vencimento_boleto').in('id', lote);
                if (error) throw error;
                return (data ?? []) as Array<{ id: number; uc_codigo: string | null; mes_referencia: string | null; valor_boleto_vigor: number | string | null; vencimento_boleto: string | null }>;
            })).then(r => r.flat()),
            todasAsLinhas<{ numero_uc: string | null; nome_cliente: string | null; cpf_cnpj_cliente: string | null; cpf_cnpj_titular: string | null; situacao: string | null }>(() =>
                supabase.from('unidades_consumidoras').select('id, numero_uc, nome_cliente, cpf_cnpj_cliente, cpf_cnpj_titular, situacao').order('id')
            ),
        ]);
        const ucDaFatura = new Map(faturas.map(f => [Number(f.id), f.uc_codigo]));
        const detalhe = new Map(faturas.map(f => [Number(f.id), {
            fatura_id: Number(f.id), uc_codigo: f.uc_codigo, mes_referencia: f.mes_referencia,
            valor: f.valor_boleto_vigor == null ? null : Number(f.valor_boleto_vigor), vencimento: f.vencimento_boleto,
        }]));
        const faturasPorCpf = new Map<string, NonNullable<DemandaEsteira['faturas_abertas']>>();
        for (const [cpf, d] of debitos ?? []) {
            // Fatura de UC com distrato vivo é tratada em Cancelamentos: vai marcada.
            const doDistrato = new Set((d.faturas_distrato ?? []).map(Number));
            const lista = d.faturas.map(id => {
                const f = detalhe.get(Number(id));
                return f && doDistrato.has(Number(id)) ? { ...f, distrato: true } : f;
            }).filter((x): x is NonNullable<typeof x> => !!x)
                .sort((a, b) => String(a.vencimento ?? '').localeCompare(String(b.vencimento ?? '')));
            if (lista.length) faturasPorCpf.set(digitos(cpf), lista);
        }
        // Cliente primeiro: o titular só vale se o CPF não é cliente de UC nenhuma —
        // senão o cliente X herdava UCs de outro cliente onde X é só o titular.
        const agrupar = (campo: 'cpf_cnpj_cliente' | 'cpf_cnpj_titular') => {
            const m = new Map<string, typeof cadastro>();
            for (const u of cadastro) {
                const cpf = digitos(u[campo]);
                if (!cpf) continue;
                if (!m.has(cpf)) m.set(cpf, []);
                m.get(cpf)!.push(u);
            }
            return m;
        };
        const cadastroPorCpf = agrupar('cpf_cnpj_cliente');
        // Nome só de onde o CPF é o cliente — como titular, o nome da UC é de outra
        // pessoa. As canceladas só entram se não houver outra.
        const nomes = new Map<string, string>();
        for (const [cpf, ucs] of cadastroPorCpf) {
            const vivas = ucs.filter(u => !/^cancelad/i.test(String(u.situacao ?? '')));
            const lista = [...new Set((vivas.length ? vivas : ucs).map(u => String(u.nome_cliente ?? '').trim()).filter(Boolean))];
            if (lista.length) nomes.set(cpf, lista.join(' / '));
        }
        for (const [cpf, ucs] of agrupar('cpf_cnpj_titular')) if (!cadastroPorCpf.has(cpf)) cadastroPorCpf.set(cpf, ucs);
        const cpfs = new Set(cpfsDosCards.map(digitos).filter(Boolean));
        const debitoPorCpf = new Map(debitos ? [...debitos.entries()].map(([c, d]) => [digitos(c), d]) : []);
        // Sem dívida e sem cadastro pelo CPF: as UCs das faturas do cliente.
        const semUc = [...new Set(cpfsDosCards.map(digitos).filter(c => c && !cadastroPorCpf.has(c) && !debitoPorCpf.get(c)?.faturas.length))];
        const lotesCpf: string[][] = [];
        for (let i = 0; i < semUc.length; i += 200) lotesCpf.push(semUc.slice(i, i + 200));
        const ucsDasFaturas = new Map<string, string[]>();
        for (const f of (await Promise.all(lotesCpf.map(lote =>
            todasAsLinhas<{ uc_codigo: string | null; cliente_cpf_cnpj: string | null }>(() =>
                supabase.from('faturas').select('id, uc_codigo, cliente_cpf_cnpj')
                    .in('cliente_cpf_cnpj', lote).neq('status_fatura', 'Excluído').order('id')
            )
        ))).flat()) {
            const cpf = digitos(f.cliente_cpf_cnpj);
            if (!ucsDasFaturas.has(cpf)) ucsDasFaturas.set(cpf, []);
            ucsDasFaturas.get(cpf)!.push(String(f.uc_codigo ?? ''));
        }

        const resultado = new Map<string, Array<{ numero: string; cancelada?: boolean }>>();
        for (const cpf of cpfs) {
            const devendo = (debitoPorCpf.get(cpf)?.faturas ?? []).map(id => ucDaFatura.get(Number(id)));
            const ucs = escolherUcsDoCliente(devendo, cadastroPorCpf.get(cpf) ?? [], ucsDasFaturas.get(cpf) ?? []);
            if (ucs.length) resultado.set(cpf, ucs);
        }
        return { ucs: resultado, faturas: faturasPorCpf, nomes };
    } catch (e) {
        console.error('UCs dos clientes indisponíveis — cards de cobrança sem UC:', e);
        return { ucs: new Map(), faturas: new Map(), nomes: new Map() };
    }
}

/**
 * Último contato registrado de cada cliente, pelo id de `cobranca_clientes`.
 * É o que mostra quem teve contato e ficou sem data (#6165). `null` se a
 * consulta falhar — o painel segue sem a fila "Sem próximo contato".
 */
interface HistoricoClientes {
    /** Último contato (só tipo 'contato') — fila "Sem próximo contato" e dono pelo contato. */
    ultimos: Map<string, UltimoContato>;
    /** Última ação (contato ou mudança de status) — o "parado" conta daqui (#6211). */
    ultimaAcao: Map<string, string>;
    /** O que foi feito hoje, por cliente — "Trabalhadas hoje" (#6211). */
    hoje: Map<string, Array<{ por: string | null; por_id: string | null; acao: string; em: string }>>;
    /** Quando o cliente passou a "negociado" pela última vez — o acordo só cobre a dívida de antes. */
    negociadoEm: Map<string, string>;
}

async function carregarUltimosContatos(agora: Date): Promise<HistoricoClientes | null> {
    try {
        const linhas = await todasAsLinhas<{
            cobranca_cliente_id: string; contato_em: string | null; created_at: string;
            user_nome: string | null; user_id: string | null; tipo_evento: string; status_novo: string | null;
        }>(() =>
            supabase.from('cobranca_negociacao_historico')
                .select('cobranca_cliente_id, contato_em, created_at, user_nome, user_id, tipo_evento, status_novo')
                .in('tipo_evento', ['contato', 'status'])
                .order('id')
        );
        const ultimos = new Map<string, UltimoContato>();
        const ultimaAcao = new Map<string, string>();
        const hoje = new Map<string, Array<{ por: string | null; por_id: string | null; acao: string; em: string }>>();
        const negociadoEm = new Map<string, string>();
        const diaDeHoje = diaLocal(agora.toISOString());
        for (const l of linhas) {
            const em = l.tipo_evento === 'contato' ? (l.contato_em ?? l.created_at) : l.created_at;
            if (l.tipo_evento === 'contato') {
                const atual = ultimos.get(l.cobranca_cliente_id);
                if (!atual || Date.parse(em) > Date.parse(atual.em)) {
                    ultimos.set(l.cobranca_cliente_id, { em, por: l.user_nome, user_id: l.user_id });
                }
            }
            if (l.tipo_evento === 'status' && l.status_novo === 'negociado') {
                const n = negociadoEm.get(l.cobranca_cliente_id);
                if (!n || Date.parse(l.created_at) > Date.parse(n)) negociadoEm.set(l.cobranca_cliente_id, l.created_at);
            }
            const ant = ultimaAcao.get(l.cobranca_cliente_id);
            if (!ant || Date.parse(l.created_at) > Date.parse(ant)) ultimaAcao.set(l.cobranca_cliente_id, l.created_at);
            // Hoje = registrado hoje (created_at), não a data informada do contato.
            if (diaLocal(l.created_at) === diaDeHoje) {
                const lista = hoje.get(l.cobranca_cliente_id) ?? [];
                lista.push({
                    por: l.user_nome,
                    por_id: l.user_id,
                    acao: l.tipo_evento === 'contato' ? 'contato' : `status: ${l.status_novo ?? 'alterado'}`,
                    em: l.created_at,
                });
                hoje.set(l.cobranca_cliente_id, lista);
            }
        }
        return { ultimos, ultimaAcao, hoje, negociadoEm };
    } catch (e) {
        console.error('Últimos contatos indisponíveis — painel sem a fila "Sem próximo contato":', e);
        return null;
    }
}

/**
 * Carrega a esteira inteira, já normalizada e ordenada.
 *
 * O filtro de status só chega ao banco na régua (`cobranca_acao_manual`), que é
 * a única origem com estado de execução próprio. Nas demais, "pendente" é o que
 * a própria derivação do estado significa — um documento assinado e enviado
 * simplesmente não vira demanda.
 *
 * `cobranca_clientes` vem inteira, sem filtrar status encerrado no banco: as
 * linhas encerradas não viram demanda (o normalizador as descarta), mas são
 * necessárias para exibir o status de negociação das OUTRAS filas. Um cliente
 * que já pagou pode ter uma notificação de mora ainda aberta, e a tela precisa
 * mostrar isso.
 */
export async function fetchEsteira(filtros: EsteiraFiltros = {}): Promise<DemandaEsteira[]> {
    const agora = new Date();

    const [acoes, mora, advertencias, distratos, clientes, debitos, ultimosContatos] = await Promise.all([
        todasAsLinhas<CobrancaAcaoManual>(() => {
            let q = supabase.from('cobranca_acao_manual').select('*');
            // Sem filtro explícito a esteira mostra o que há por fazer.
            if (filtros.status !== '') q = q.eq('status', filtros.status || 'pendente');
            return q;
        }),
        todasAsLinhas<LogDocumento>(() =>
            supabase.from('notificacao_mora_logs').select('*').is('cancelado_em', null)
        ),
        todasAsLinhas<LogDocumento>(() =>
            supabase.from('comunicado_advertencia_logs').select('*').is('cancelado_em', null)
        ),
        todasAsLinhas<LogDistrato>(() =>
            supabase.from('termo_cancelamento_logs').select('*').is('cancelado_em', null)
        ),
        todasAsLinhas<LinhaCobrancaCliente>(() =>
            supabase.from('cobranca_clientes').select('*')
        ),
        carregarDebitos(),
        carregarUltimosContatos(agora),
    ]);

    // Notificação extrajudicial anterior por CPF/CNPJ — alimenta o aviso "já
    // notificada" do D+17. A mora já está carregada, então isso sai de graça:
    // a ManuaisTab precisava de uma query extra só para montar este mapa.
    const notificacaoPorCpf = new Map<string, { enviado_cliente_em: string; resultado_verificacao: string | null }>();
    for (const n of [...mora].sort(ordenarPorEnvioDesc)) {
        if (!n.cpf_cnpj_cliente || !n.enviado_cliente_em) continue;
        if (!notificacaoPorCpf.has(n.cpf_cnpj_cliente)) {
            notificacaoPorCpf.set(n.cpf_cnpj_cliente, {
                enviado_cliente_em: n.enviado_cliente_em,
                resultado_verificacao: n.resultado_verificacao ?? null,
            });
        }
    }

    // Notificação de mora mais recente por CPF/CNPJ (a cancelada não conta) —
    // tira da fila "encaminhada" quem já foi notificado depois do encaminhamento.
    const notificacaoGeradaPorCpf = new Map<string, string>();
    for (const n of mora) {
        if (!n.cpf_cnpj_cliente || n.cancelado_em) continue;
        const atual = notificacaoGeradaPorCpf.get(n.cpf_cnpj_cliente);
        if (!atual || Date.parse(n.created_at) > Date.parse(atual)) notificacaoGeradaPorCpf.set(n.cpf_cnpj_cliente, n.created_at);
    }

    // Quem deve e nunca teve linha de cobrança: sem isto não aparecia em
    // fila nenhuma fora dos dias exatos da régua (07/10/2026).
    const comLinha = new Set(clientes.map(c => c.cpf_cnpj_cliente));
    const soDevem = debitos
        ? [...debitos.entries()]
            .filter(([cpf, d]) => !comLinha.has(cpf) && d.faturas.length)
            .map(([cpf, d]) => linhaDeQuemSoDeve(cpf, d))
        : [];

    // Por CPF (só dígitos): dono da carteira, quem está no SPC e quem negociou com data.
    const dig = (v: unknown) => String(v ?? '').replace(/\D/g, '');
    const donoCarteira = new Map<string, string>();
    const negativados = new Set<string>();
    const negociadoAte = new Map<string, string>();
    const clientePorCpf = new Map<string, string>();
    // Desde quando — só a régua da dívida de antes some (#6211).
    const negativadoDesde = new Map<string, string>();
    const negociadoDesde = new Map<string, string>();
    for (const c of clientes) {
        const cpf = dig(c.cpf_cnpj_cliente);
        if (!cpf) continue;
        clientePorCpf.set(cpf, c.id);
        if (c.responsavel_cobranca_id) donoCarteira.set(cpf, c.responsavel_cobranca_id);
        if (c.negativacao_realizada_em && !c.negativacao_retirada_em) {
            negativados.add(cpf);
            negativadoDesde.set(cpf, c.negativacao_realizada_em);
        }
        if (c.status_negociacao === 'negociado' && c.data_pagamento_negociado) {
            negociadoAte.set(cpf, c.data_pagamento_negociado);
            const em = ultimosContatos?.negociadoEm.get(c.id);
            if (em) negociadoDesde.set(cpf, em);
        }
    }

    const acoesEnriquecidas = acoes.map(a => ({
        ...a,
        // Etapa sem responsável (rodízio vazio) é do dono da carteira (#6211): o
        // cliente em negociação aparecia "sem dono" no D+17/D+24.
        responsavel_id: a.responsavel_id ?? donoCarteira.get(dig(a.cpf_cnpj_cliente)) ?? null,
        faturas: Array.isArray(a.faturas) ? a.faturas : [],
        notificacao_previa: a.cpf_cnpj_cliente ? notificacaoPorCpf.get(a.cpf_cnpj_cliente) ?? null : null,
    }));

    // Faturas vencidas em aberto de todo mundo: a etapa da régua só fica se
    // alguma fatura dela ainda está aqui. `null` = não deu para saber.
    const faturasVencidas = debitos
        ? new Set([...debitos.values()].flatMap(d => d.faturas))
        : null;
    // As mesmas, por cliente: a etapa da régua e a notificação de mora só ficam
    // se a dívida ainda é DESTE cliente (24/10/2026).
    const faturasPorCpf = debitos
        ? new Map([...debitos.entries()].map(([cpf, d]) => [dig(cpf), new Set(d.faturas)]))
        : null;

    const normalizadas: DemandaEsteira[] = filtrarMoraSemDebito(filtrarReguaComDebito([
        ...acoesEnriquecidas.map(a => normalizarAcaoManual(a, agora)),
        ...mora.map(m => normalizarDocumento(m, 'mora', agora)),
        ...advertencias.map(a => normalizarDocumento(a, 'advertencia', agora)),
        ...distratos.map(d => normalizarDistrato(d, agora)),
        ...[...clientes, ...soDevem].map(c => normalizarCobrancaCliente(
            c, agora,
            debitos ? debitos.get(c.cpf_cnpj_cliente) ?? null : undefined,
            ultimosContatos?.ultimos.get(c.id) ?? null,
            notificacaoGeradaPorCpf.get(c.cpf_cnpj_cliente) ?? null,
        )),
    ].filter((d): d is DemandaEsteira => d !== null)
     .filter(d => !foraDaReguaPorTratamento(d, negativados, negociadoAte, agora, { negativado: negativadoDesde, negociado: negociadoDesde })),
    faturasVencidas, faturasPorCpf), faturasVencidas, faturasPorCpf);
    // Negativado que "não deve" mas tem dívida no mesmo nome em outro CPF não
    // ganha "Retirar negativação" — ver `marcarDividaEmOutroCpf` (25/10/2026).
    const porNome = debitos ? dividaPorNome(debitos.entries()) : null;
    const demandas = porNome ? normalizadas.map(d => marcarDividaEmOutroCpf(d, porNome)) : normalizadas;

    // O que é do distrato sai da rotina de cobrança — ver `marcarDistrato`.
    const faturasDistrato = debitos
        ? new Set([...debitos.values()].flatMap(d => d.faturas_distrato ?? []))
        : null;

    const statusPorCpf = new Map(
        clientes.map(c => [c.cpf_cnpj_cliente, c.status_negociacao as CobrancaStatusNegociacao])
    );
    const proximoPorCpf = new Map(
        clientes
            .filter(c => !!c.proximo_contato_em)
            .map(c => [c.cpf_cnpj_cliente, c.proximo_contato_em as string])
    );
    const [atribuicoes, ucsEFaturas] = await Promise.all([carregarAtribuicoes(), carregarUcsPorCpf(debitos, demandas.filter(d => !d.uc_numero).map(d => d.cpf_cnpj_cliente))]);
    const ucsPorCpf = ucsEFaturas.ucs;
    // Sem dívida, o card da cobrança mostrava o nome guardado na linha — o do
    // primeiro cliente visto com o documento. O CNPJ da HSolar paga UCs de várias
    // empresas e a linha dele dizia "TECAR": a Dhaiane resolvia a Amparo e o card
    // virava Tecar (reunião de 23/10/2026). Aí vale o nome do cadastro.
    const nomeDoCadastro = (d: DemandaEsteira): string | undefined =>
        d.origem === 'cobranca_clientes' && d.cpf_cnpj_cliente && !debitos?.get(d.cpf_cnpj_cliente)?.cliente_nome
            ? ucsEFaturas.nomes.get(dig(d.cpf_cnpj_cliente))
            : undefined;
    const comStatus = demandas.map(d => ({
        ...marcarDistrato(comUcs(d, ucsPorCpf), faturasDistrato, d.cpf_cnpj_cliente ? debitos?.get(d.cpf_cnpj_cliente) : null),
        ...(nomeDoCadastro(d) ? { cliente_nome: nomeDoCadastro(d) as string } : {}),
        status_negociacao: d.cpf_cnpj_cliente ? statusPorCpf.get(d.cpf_cnpj_cliente) ?? null : null,
        atribuicao: atribuicoes.get(`${d.origem}:${d.origem_id}`) ?? null,
    }));

    // Um card por cliente e por pessoa, e fora da lista quem tem retorno
    // agendado para depois de hoje — ver normalizar.ts.
    const agrupadas = agruparPorCliente(await resolverNomes(comStatus));

    // #6211, por card: débitos ao vivo, trabalho de hoje e "parado" desde a última
    // ação. Do cliente (régua e carteira) pelo CPF; documento só pela atribuição.
    const completos = agrupadas.map(d => {
        const doCliente = d.origem === 'cobranca_acao_manual' || d.origem === 'cobranca_clientes';
        const cpf = dig(d.cpf_cnpj_cliente);
        const clienteId = doCliente && cpf ? clientePorCpf.get(cpf) : undefined;
        // Só nos cards do cliente: em documento, a cobrança em lote (36 distratos
        // de uma vez) grava andamento em todos e zerava o "parado" de todos.
        const ultima = doCliente ? maisRecente(
            d.atribuicao?.ultima_acao_em,
            ...(d.itens ?? []).map(i => i.atribuicao?.ultima_acao_em),
            clienteId ? ultimosContatos?.ultimaAcao.get(clienteId) : null,
        ) : null;
        const comAcao = aplicarUltimaAcao(d, ultima, agora);
        const hoje = clienteId ? ultimosContatos?.hoje.get(clienteId) : undefined;
        const faturas = doCliente && cpf ? ucsEFaturas.faturas.get(cpf) : undefined;
        return {
            ...comAcao,
            ...(hoje?.length ? { trabalho_hoje: hoje } : {}),
            ...(faturas?.length ? { faturas_abertas: faturas } : {}),
        };
    });
    return ordenarDemandas(marcarAgendados(completos, proximoPorCpf, agora));
}

/**
 * Quem assumiu cada demanda derivada.
 *
 * Degrada em silêncio se `esteira_atribuicoes` ainda não existe no banco: a
 * tela foi construída antes da migration e precisa funcionar dos dois lados —
 * sem a tabela, ninguém tem dono, que é exatamente a situação de hoje.
 */
async function carregarAtribuicoes(): Promise<Map<string, EsteiraAtribuicao>> {
    const vazio = new Map<string, EsteiraAtribuicao>();
    try {
        const linhas = await todasAsLinhas<EsteiraAtribuicao>(() =>
            supabase.from('esteira_atribuicoes').select('*')
        );
        return new Map(linhas.map(a => [`${a.origem}:${a.origem_id}`, a]));
    } catch {
        return vazio;
    }
}

/**
 * Assume (ou repassa) uma demanda derivada. Upsert pela chave origem+origem_id,
 * que é única — duas pessoas clicando junto não criam duas linhas.
 */
export async function assumirDemanda(
    demanda: DemandaEsteira,
    responsavelId: string,
    porId: string,
): Promise<void> {
    const agora = new Date().toISOString();
    const { error } = await supabase
        .from('esteira_atribuicoes')
        .upsert({
            origem: demanda.origem,
            origem_id: demanda.origem_id,
            responsavel_id: responsavelId,
            assumido_em: agora,
            assumido_por: porId,
            updated_at: agora,
        }, { onConflict: 'origem,origem_id' });
    if (error) throw error;
}

/** Registra o que foi feito. É isto que responde "está sendo trabalhado?". */
export async function registrarAndamento(
    demanda: DemandaEsteira,
    porId: string,
    observacao: string,
    opcoes: {
        /**
         * Se ninguém assumiu, quem registra vira dono (padrão: sim — quem mexeu é
         * quem está cuidando). Cobrança em lote passa `false`: cobrar a Dhaiane
         * pelos 36 distratos dela não pode tornar quem clicou dono dos 36.
         */
        assumirSeLivre?: boolean;
    } = {},
): Promise<void> {
    const assumir = opcoes.assumirSeLivre ?? true;
    const atual = demanda.atribuicao;
    const agora = new Date().toISOString();
    const { error } = await supabase
        .from('esteira_atribuicoes')
        .upsert({
            origem: demanda.origem,
            origem_id: demanda.origem_id,
            // Nunca apaga um dono que já existe; só cria um se `assumir`.
            responsavel_id: atual?.responsavel_id ?? (assumir ? porId : null),
            assumido_em: atual?.assumido_em ?? (assumir ? agora : null),
            assumido_por: atual?.assumido_por ?? (assumir ? porId : null),
            ultima_acao_em: agora,
            ultima_acao_por: porId,
            observacao: observacao.trim() || null,
            updated_at: agora,
        }, { onConflict: 'origem,origem_id' });
    if (error) throw error;
}

/**
 * Pode corrigir ou apagar a anotação: quem escreveu, ou adm/gerente (#6290).
 * O banco aceita de qualquer analista — isto é só para ninguém reescrever,
 * sem querer, o andamento de um colega que continua assinado por ele.
 */
export function podeCorrigirAndamento(
    atribuicao: EsteiraAtribuicao | null | undefined,
    usuarioId: string | undefined,
    role: string | null | undefined,
): boolean {
    if (!atribuicao?.ultima_acao_em || !usuarioId) return false;
    return atribuicao.ultima_acao_por === usuarioId || role === 'adm' || role === 'gerente';
}

/**
 * A linha só muda se o andamento ainda for o que a tela mostrou. Sem isto, quem
 * deixou a esteira aberta desde cedo apagaria a anotação que um colega fez
 * depois — e a do colega não tem cópia: a tabela guarda só a última.
 */
async function mudarAndamentoSeIgual(
    atribuicao: EsteiraAtribuicao,
    mudanca: Partial<EsteiraAtribuicao>,
): Promise<void> {
    let consulta = supabase
        .from('esteira_atribuicoes')
        .update({ ...mudanca, updated_at: new Date().toISOString() })
        .eq('id', atribuicao.id);
    // `ultima_acao_por` vira nulo se o usuário for apagado (ON DELETE SET NULL).
    for (const coluna of ['ultima_acao_em', 'ultima_acao_por'] as const) {
        const valor = atribuicao[coluna];
        consulta = valor ? consulta.eq(coluna, valor) : consulta.is(coluna, null);
    }
    const { data, error } = await consulta.select('id');
    if (error) throw error;
    if (!data?.length) {
        throw new Error('Alguém registrou outro andamento depois que a tela carregou. Atualize a esteira e confira antes de mexer.');
    }
}

/**
 * Corrige o texto do último andamento — anotação preenchida errado (#6290).
 *
 * Não é um andamento novo: quem escreveu continua com a data de quando anotou.
 * Se quem corrige é outra pessoa (gerente), a anotação passa a ser dela, com a
 * data de hoje: o card mostra quem escreveu o texto que está ali, e não
 * atribui ao colega uma frase que ele não escreveu.
 */
export async function editarAndamento(
    atribuicao: EsteiraAtribuicao,
    porId: string,
    observacao: string,
): Promise<void> {
    const texto = observacao.trim();
    if (!texto) throw new Error('A anotação não pode ficar vazia — para tirá-la, use Excluir.');
    const outraPessoa = atribuicao.ultima_acao_por !== porId;
    await mudarAndamentoSeIgual(atribuicao, {
        observacao: texto,
        ...(outraPessoa ? { ultima_acao_por: porId, ultima_acao_em: new Date().toISOString() } : {}),
    });
}

/**
 * Apaga o último andamento: some a anotação, quem anotou e quando. Quem assumiu
 * a demanda continua com ela — excluir uma frase errada não devolve o trabalho
 * para a fila. A anotação anterior não volta: a tabela só guarda a última.
 */
export async function excluirAndamento(atribuicao: EsteiraAtribuicao): Promise<void> {
    await mudarAndamentoSeIgual(atribuicao, {
        observacao: null,
        ultima_acao_em: null,
        ultima_acao_por: null,
    });
}

/**
 * Busca a UC inteira a partir do número que a demanda carrega.
 *
 * As origens guardam só `uc_numero` (texto), mas `UCManagementModal` precisa do
 * registro completo. Devolve `null` quando a UC não existe mais ou quando o
 * número não tem cadastro — caso real: número novo da distribuidora que nunca
 * foi recadastrado.
 */
export async function buscarUnidadePorNumero(numero: string): Promise<Record<string, unknown> | null> {
    const { data, error } = await supabase
        .from('unidades_consumidoras')
        .select('*')
        .eq('numero_uc', numero)
        .limit(1)
        .maybeSingle();
    if (error) throw error;
    return data ?? null;
}

/** Devolve a demanda para a fila aberta. */
export async function liberarDemanda(demanda: DemandaEsteira): Promise<void> {
    const agora = new Date().toISOString();
    const { error } = await supabase
        .from('esteira_atribuicoes')
        .upsert({
            origem: demanda.origem,
            origem_id: demanda.origem_id,
            responsavel_id: null,
            assumido_em: null,
            assumido_por: null,
            updated_at: agora,
        }, { onConflict: 'origem,origem_id' });
    if (error) throw error;
}

/** Mais recente primeiro. Empate e nulo vão para o fim. */
function ordenarPorEnvioDesc(a: LogDocumento, b: LogDocumento): number {
    return (b.enviado_cliente_em ?? '').localeCompare(a.enviado_cliente_em ?? '');
}

/**
 * Preenche `responsavel_nome` numa query só.
 *
 * ⚠️ `profiles.id` é TEXT no banco (não uuid), então a ligação com os uuids de
 * responsável é feita em JS — mesma armadilha documentada em fetchAcoesManuais.
 */
async function resolverNomes(demandas: DemandaEsteira[]): Promise<DemandaEsteira[]> {
    const ids = [...new Set(
        demandas.flatMap(d => [
            d.responsavel_id,
            d.atribuicao?.responsavel_id,
            d.atribuicao?.ultima_acao_por,
        ]).filter((v): v is string => !!v)
    )];
    if (!ids.length) return demandas;

    const { data } = await supabase.from('profiles').select('id, full_name').in('id', ids);
    const nomePorId = new Map((data ?? []).map((p: any) => [String(p.id), p.full_name as string]));
    const nome = (id?: string | null) => (id ? nomePorId.get(id) ?? null : null);

    return demandas.map(d => ({
        ...d,
        responsavel_nome: nome(d.responsavel_id),
        atribuicao: d.atribuicao
            ? {
                ...d.atribuicao,
                responsavel_nome: nome(d.atribuicao.responsavel_id),
                ultima_acao_por_nome: nome(d.atribuicao.ultima_acao_por),
            }
            : null,
    }));
}

/**
 * Aplica os filtros da tela sobre o que já está em memória.
 *
 * Fica fora do `fetchEsteira` de propósito: trocar um filtro não precisa bater
 * no banco de novo, e os KPIs continuam contando a esteira inteira enquanto a
 * lista mostra o recorte.
 *
 * `poolContato` (quem faz o D+4/D+10): no filtro de uma pessoa, entra também
 * o que é compartilhado com ela — ver `compartilhadaCom`.
 */
export function filtrarDemandas(
    demandas: DemandaEsteira[],
    filtros: EsteiraFiltros,
    poolContato: string[] = [],
): DemandaEsteira[] {
    const termo = filtros.search?.trim().toLowerCase() ?? '';
    const soDigitos = termo.replace(/\D/g, '');

    // `fetchEsteira` já entrega na ordem padrão; só reordena se a pessoa pediu a outra.
    const ordenadas = filtros.ordem && filtros.ordem !== 'recente' ? ordenarDemandas(demandas, filtros.ordem) : demandas;
    return ordenadas.filter(d => {
        // Agendado só aparece quando a pessoa pede para ver os agendados.
        if (filtros.apenasTrabalhadasHoje) {
            if (!trabalhadaHoje(d, new Date())) return false;
        } else if (!!d.agendado !== !!filtros.apenasAgendados) return false;
        // Distrato também: só aparece no filtro dele.
        if (!!d.com_distrato !== !!filtros.apenasDistrato) return false;
        if (filtros.grupo && d.grupo !== filtros.grupo
            && !(d.itens ?? []).some(i => i.grupo === filtros.grupo)) return false;
        // Card agrupado entra no filtro de qualquer fila que ele cobre.
        if (filtros.fila && !filasDa(d).includes(filtros.fila)) return false;
        // Dono efetivo, não o criador do documento — ver `donoEfetivo`.
        // Etapa de outra pessoa no card do cliente também é dela (#6209).
        if (filtros.responsavelId && !ehDaPessoa(d, filtros.responsavelId)
            && !compartilhadaCom(d, filtros.responsavelId, poolContato)) return false;
        // Negativado é acompanhamento: não é "sem dono" nem "parado".
        if (filtros.apenasSemDono && (donoEfetivo(d) || d.acompanhamento)) return false;
        // "Paradas +7d" (min 8) usa a mesma regra do contador, que inclui a dívida
        // antiga sem retorno marcado (#6211); 15 e 30 dias seguem pelo número.
        if (filtros.minDiasParado && (d.acompanhamento
            || !(d.dias_parado >= filtros.minDiasParado || (filtros.minDiasParado <= 8 && estaParado(d))))) return false;
        if (filtros.statusNegociacao && d.status_negociacao !== filtros.statusNegociacao) return false;

        if (termo) {
            const campos = [
                d.cliente_nome ?? '',
                d.cpf_cnpj_cliente ?? '',
                d.uc_numero ?? '',
                ...(d.ucs ?? []).map(u => u.numero),
            ];
            // Busca por documento/UC funciona com ou sem pontuação. Campo a campo:
            // juntos, os dígitos do fim do CPF com os do começo da UC casavam à toa.
            const casaTexto = campos.join(' ').toLowerCase().includes(termo);
            const casaNumero = soDigitos.length >= 3
                && campos.some(c => c.replace(/\D/g, '').includes(soDigitos));
            if (!casaTexto && !casaNumero) return false;
        }

        return true;
    });
}

export function kpisDaEsteira(demandas: DemandaEsteira[]): EsteiraKPIs {
    return calcularKPIs(demandas);
}

/**
 * Quantas demandas estão com esta pessoa — decide se a tela abre em "Meus
 * itens" ou em "Todos", mesma regra da fila de contato humano.
 */
export function contarDemandasDoUsuario(demandas: DemandaEsteira[], userId: string, poolContato: string[] = []): number {
    // Negativado é acompanhamento: não entra no "Meus itens (N)".
    return demandas.filter(d => d.status === 'pendente' && !d.agendado && !d.com_distrato && !d.acompanhamento
        && (ehDaPessoa(d, userId) || compartilhadaCom(d, userId, poolContato))).length;
}

/** Quantos clientes estão fora da lista esperando o retorno agendado. */
export function contarAgendados(demandas: DemandaEsteira[]): number {
    return demandas.filter(d => d.agendado && !d.com_distrato).length;
}

/** Quantas pendências estão fora da lista por serem do distrato. */
export function contarDistrato(demandas: DemandaEsteira[]): number {
    return demandas.filter(d => d.com_distrato && d.status === 'pendente' && !d.agendado).length;
}

// ─────────────────────────────────────────────────────────────────────────────
// Atendimento do cliente (#6147)
// ─────────────────────────────────────────────────────────────────────────────

export interface DadosContato {
    canal: string;
    /** 'YYYY-MM-DD' — o dia em que o contato aconteceu. */
    data: string;
    resultado?: string | null;
    observacao: string;
    /** Como ficou o cliente: data de retorno ou status (#6165). */
    desfecho: CamposDesfecho;
}

/**
 * Registra o contato com o cliente: data, observação e como ele ficou.
 *
 * O status do desfecho (negociando, negociado, pago) é gravado ANTES: a função
 * do banco só aceita contato sem próximo contato quando o cliente já está pago
 * ou na negativação (#6165). Depois, numa transação, ela grava o histórico, a
 * data do retorno e o dono para quem não tinha — e o gatilho fecha as etapas de
 * contato (D+4 e D+10) do cliente para todo mundo.
 *
 * Data ao meio-dia de Brasília: 'YYYY-MM-DD' lido como UTC voltaria um dia.
 * Devolve a linha do cliente já atualizada.
 */
export async function registrarContatoCliente(
    cpfCnpj: string,
    clienteNome: string | null,
    dados: DadosContato,
    quem: { id: string; nome: string },
): Promise<CobrancaCliente> {
    const statusNovo = statusDoDesfecho(dados.desfecho.desfecho);
    if (statusNovo) {
        const cliente = await ensureCobrancaCliente(cpfCnpj, clienteNome);
        if (!cliente) throw new Error('Cliente sem CPF/CNPJ — não dá para mudar o status.');
        // "Negociando" de novo não muda nada; negociado e pago trazem valor e
        // data novos, então gravam mesmo repetidos.
        if (statusNovo !== 'negociando' || cliente.status_negociacao !== 'negociando') {
            await atualizarStatusCobrancaCliente({
                cobrancaClienteId: cliente.id,
                statusAnterior: cliente.status_negociacao,
                statusNovo,
                userId: quem.id,
                userNome: quem.nome,
                camposExtras: camposDoDesfecho(dados.desfecho, quem),
            });
        }
    }

    const { data, error } = await supabase.rpc('registrar_contato_cobranca', {
        p_cpf_cnpj: cpfCnpj,
        p_cliente_nome: clienteNome,
        p_canal: dados.canal,
        p_resultado: dados.resultado || null,
        p_contato_em: new Date(`${dados.data}T12:00:00-03:00`).toISOString(),
        p_proximo_contato_em: retornoDoDesfecho(dados.desfecho),
        p_observacao: dados.observacao.trim() || null,
    });
    if (error) throw error;
    return data as CobrancaCliente;
}

/** Etapas da régua ainda pendentes dentro de um card (agrupado ou não). */
export function pendenciasDaRegua(d: DemandaEsteira): CobrancaAcaoManual[] {
    return (d.itens ?? [d])
        .filter(i => i.origem === 'cobranca_acao_manual' && i.status === 'pendente')
        .map(i => i.bruto as CobrancaAcaoManual);
}

/**
 * Assume o cliente: vira dono dele na carteira de cobrança. O gatilho do banco
 * traz junto as etapas de contato pendentes — sai da fila de quem tinha.
 */
export async function assumirCliente(cpfCnpj: string, clienteNome: string | null, userId: string): Promise<void> {
    const cliente = await ensureCobrancaCliente(cpfCnpj, clienteNome);
    if (!cliente) throw new Error('Cliente sem CPF/CNPJ — não dá para assumir pela carteira.');
    const { error } = await supabase
        .from('cobranca_clientes')
        .update({ responsavel_cobranca_id: userId })
        .eq('id', cliente.id);
    if (error) throw error;
}

/**
 * Registra que a negativação foi retirada do SPC. Não muda o status: o cliente
 * continua "pago" ou "negociado" — a retirada é um fato a mais, com quem e
 * quando, e fica no histórico.
 */
export async function registrarRetiradaNegativacao(
    cobrancaClienteId: string,
    statusAtual: string,
    userId: string,
    userNome: string,
): Promise<void> {
    const agora = new Date().toISOString();
    const { error } = await supabase
        .from('cobranca_clientes')
        .update({
            negativacao_retirada_em: agora,
            negativacao_retirada_por_id: userId || null,
            negativacao_retirada_por_nome: userNome,
        })
        .eq('id', cobrancaClienteId);
    if (error) throw error;

    const { error: erroHistorico } = await supabase
        .from('cobranca_negociacao_historico')
        .insert({
            cobranca_cliente_id: cobrancaClienteId,
            user_id: userId || null,
            user_nome: userNome,
            status_anterior: statusAtual,
            status_novo: statusAtual,
            tipo_evento: 'observacao',
            observacao: 'Negativação retirada do SPC/Serasa.',
        });
    if (erroHistorico) console.error('Retirada gravada, mas o histórico falhou:', erroHistorico);
}

/**
 * Procura cliente fora do painel (#6211): por nome, CPF/CNPJ ou UC, no cadastro de
 * cobrança e no das UCs. É o caminho para quem pagou e não tem mais card, para
 * abrir o "Gerenciar Cobrança" e registrar o pagamento.
 */
export async function procurarClientesCobranca(termo: string): Promise<Array<{ cpf: string; nome: string | null; ucs: string[] }>> {
    const t = termo.trim();
    if (t.length < 3) return [];
    const dig = t.replace(/\D/g, '');
    const porDigitos = dig.length >= 4 && dig.length === t.replace(/[\s.\-/]/g, '').length;
    const [cc, ucs] = await Promise.all([
        porDigitos
            ? supabase.from('cobranca_clientes').select('cpf_cnpj_cliente, cliente_nome').like('cpf_cnpj_cliente', `%${dig}%`).limit(15)
            : supabase.from('cobranca_clientes').select('cpf_cnpj_cliente, cliente_nome').ilike('cliente_nome', `%${t}%`).limit(15),
        porDigitos
            ? supabase.from('unidades_consumidoras').select('numero_uc, nome_cliente, cpf_cnpj_cliente')
                .or(`numero_uc_norm.like.%${dig}%,cpf_cnpj_cliente.like.%${dig}%`).limit(15)
            : supabase.from('unidades_consumidoras').select('numero_uc, nome_cliente, cpf_cnpj_cliente').ilike('nome_cliente', `%${t}%`).limit(15),
    ]);
    if (cc.error) throw cc.error;
    if (ucs.error) throw ucs.error;
    const porCpf = new Map<string, { cpf: string; nome: string | null; ucs: Set<string> }>();
    const add = (cpfBruto: string | null, nome: string | null, uc?: string | null) => {
        const cpf = String(cpfBruto ?? '').replace(/\D/g, '');
        if (!cpf) return;
        const atual = porCpf.get(cpf) ?? { cpf, nome, ucs: new Set<string>() };
        if (!atual.nome && nome) atual.nome = nome;
        if (uc) atual.ucs.add(uc);
        porCpf.set(cpf, atual);
    };
    for (const c of cc.data ?? []) add((c as any).cpf_cnpj_cliente, (c as any).cliente_nome);
    for (const u of ucs.data ?? []) add((u as any).cpf_cnpj_cliente, (u as any).nome_cliente, (u as any).numero_uc);
    return [...porCpf.values()].slice(0, 15).map(x => ({ cpf: x.cpf, nome: x.nome, ucs: [...x.ucs] }));
}

/**
 * Clientes trabalhados hoje no "Gerenciar Cobrança" — contato ou mudança de
 * status —, tenham card no painel ou não (#6211). A maioria some do painel depois
 * do contato (pagou, ou ficou com retorno marcado sem nada pendente), e contar só
 * os cards dava 2 para 12 clientes atendidos.
 */
export async function trabalhoDoDia(): Promise<Array<{ cpf: string; nome: string | null; eventos: Array<{ por: string | null; acao: string; em: string }> }>> {
    const inicio = new Date();
    inicio.setHours(0, 0, 0, 0);
    const { data, error } = await supabase
        .from('cobranca_negociacao_historico')
        .select('created_at, user_nome, tipo_evento, status_novo, cliente:cobranca_clientes(cpf_cnpj_cliente, cliente_nome)')
        .in('tipo_evento', ['contato', 'status'])
        .gte('created_at', inicio.toISOString())
        .order('created_at');
    if (error) throw error;
    const porCpf = new Map<string, { cpf: string; nome: string | null; eventos: Array<{ por: string | null; acao: string; em: string }> }>();
    for (const l of (data ?? []) as any[]) {
        const cpf = String(l.cliente?.cpf_cnpj_cliente ?? '').replace(/\D/g, '');
        if (!cpf) continue;
        const atual = porCpf.get(cpf) ?? { cpf, nome: l.cliente?.cliente_nome ?? null, eventos: [] };
        atual.eventos.push({
            por: l.user_nome ?? null,
            acao: l.tipo_evento === 'contato' ? 'contato' : `status: ${l.status_novo ?? 'alterado'}`,
            em: l.created_at,
        });
        porCpf.set(cpf, atual);
    }
    return [...porCpf.values()];
}
